import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { components } from "react-select";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import TagSelect from "@components/fields/TagSelect";
import TextWithIcon from "@components/text/TextWithIcon";
import {
  GetJourneyWithTagsQuery,
  JourneyUpdateDocument,
  JourneyUpdateMutation,
  JourneyUpdateMutationVariables,
  TagTypeEnum
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { sortStrings } from "@utils/stringUtils";

type Journey = GetJourneyWithTagsQuery["journey"];

type FormData = {
  archivedTags: string[];
  tags: string[];
};

type Props = {
  journey: Journey;
  onError: () => void;
  onSuccess: () => void;
};

const JourneyTagsForm = ({ journey, onError, onSuccess }: Props) => {
  const intl = useIntl();

  const defaultValues: FormData = {
    archivedTags: journey.archivedTags,
    tags: journey.tags
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      archivedTags: yup.array().defined().of(yup.string().defined()).defined(),
      tags: yup.array().defined().of(yup.string().defined())
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateJourney] = useMutation<JourneyUpdateMutation, JourneyUpdateMutationVariables>(JourneyUpdateDocument);

  const tags = form.watch("tags");
  const archivedTags = form.watch("archivedTags");

  const [lastTags, setLastTags] = useState<string[]>(journey.tags);

  const submitChanges = useCallback(
    (tags: string[], archivedTags: string[]) => {
      setBaseErrors([]);

      return updateJourney({
        variables: {
          archivedTags,
          id: journey.id,
          tags
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.journeyUpdate,
          intl,
          onError,
          onSuccess,
          setBaseErrors,
          setError: form.setError,
          txBase: "errors.journey"
        });
      });
    },
    [form, intl, journey.id, onError, onSuccess, updateJourney]
  );

  const handleTagsChanged = useCallback(
    (newTags: string[] | string) => {
      newTags = newTags as string[];

      // For any tags that were removed from lastTags, add them to archivedTags if they aren't already there
      const addToArchivedTags = lastTags.filter(
        tag => !newTags.includes(tag) && !archivedTags.includes(tag) && journey.tags.includes(tag)
      );

      // For any tags that were added from lastTags, remove them from archivedTags if they are there
      const removeFromArchivedTags = newTags.filter(tag => !lastTags.includes(tag) && archivedTags.includes(tag));

      const newArchivedTags = sortStrings(
        archivedTags.filter(tag => !removeFromArchivedTags.includes(tag)).concat(addToArchivedTags)
      );

      form.setValue("archivedTags", newArchivedTags);

      setLastTags(newTags);

      submitChanges(newTags, newArchivedTags);
    },
    [archivedTags, form, journey.tags, lastTags, submitChanges]
  );

  const handleArchivedTagsChanged = useCallback(
    (newTags: string[] | string) => {
      submitChanges(tags, newTags as string[]);
    },
    [submitChanges, tags]
  );

  const archivedLabel = useMemo(
    () => (
      <TextWithIcon icon="boxArchive" iconFirst text={intl.formatMessage({ id: "forms.journey.archivedTagsLabel" })} />
    ),
    [intl]
  );

  return (
    <>
      <BaseErrors errors={baseErrors} />
      <TagSelect<FormData>
        control={form.control}
        errors={form.formState.errors.tags}
        label={intl.formatMessage({ id: "forms.journey.tagsLabel" })}
        name="tags"
        onValueChange={handleTagsChanged}
        tagType={TagTypeEnum.JOURNEY}
      />
      <TagSelect<FormData>
        color="grey"
        components={{
          ...components,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          DropdownIndicator: () => null,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          IndicatorSeparator: () => null
        }}
        control={form.control}
        errors={form.formState.errors.archivedTags}
        isClearable={false}
        isSearchable={false}
        label={archivedLabel}
        name="archivedTags"
        noOptionsMessage={() => null}
        onValueChange={handleArchivedTagsChanged}
        openMenuOnClick={false}
        tagType={TagTypeEnum.JOURNEY}
      />
    </>
  );
};

export default JourneyTagsForm;
